/// parse-helpers — `unknown` を安全に検査して branded 値へ変換する門番部品。
///
/// 方針 (#471, parse, don't validate):
/// - 自前 backend のレスポンスなので「不正値が来た」= 契約破れ。**デフォルトは throw**
///   (`reqRecord`/`reqArray`/`reqVO`/`optVO`/`voArray`)。store の catch が UI エラーに変換する。
/// - 任意フィールドの「値なし (null/undefined)」は `null` (契約破れではない)。
/// - 汚い既存データを UI で耐える箇所だけ `optVOLenient`/`voArrayDropInvalid` を
///   **名前で明示**してオプトインで使う (present-不正を null/drop + devWarn)。
/// - **CRM データ漏洩防止**: error/warn に `raw` 値を載せない。`field` と型サマリのみ。
/// 詳細は GitHub Discussion #475 (フロント FSD: API 境界の VO 適用方針)
/// https://github.com/AX-UNLEARN/LawyerPartnerCRM/discussions/475 を参照。

export class ResponseMappingError extends Error {
  constructor(
    public readonly field: string,
    public readonly reason: string,
  ) {
    super(`invalid API response at ${field}: ${reason}`);
    this.name = 'ResponseMappingError';
  }
}

/// 値そのものは載せない (CRM データ漏洩防止)。型サマリのみ返す。
function typeSummary(raw: unknown): string {
  if (raw === null) return 'null';
  if (Array.isArray(raw)) return 'array';
  return typeof raw;
}

/// dev ビルドでのみ警告。`import.meta.env` (Vite build env) 依存は shared に閉じ込め、
/// domain parser は本 helper 経由でのみ警告する (domain の FW 非依存を維持)。
export function devWarn(field: string, reason: string): void {
  if (import.meta.env.DEV) {
    console.warn(`[parse] ${field}: ${reason}`);
  }
}

// --- 構造ガード (生 TypeError を出さず ResponseMappingError 化) ---

export function reqRecord(raw: unknown, field: string): Record<string, unknown> {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new ResponseMappingError(field, `expected object, got ${typeSummary(raw)}`);
  }
  return raw as Record<string, unknown>;
}

export function reqArray(raw: unknown, field: string): unknown[] {
  if (!Array.isArray(raw)) {
    throw new ResponseMappingError(field, `expected array, got ${typeSummary(raw)}`);
  }
  return raw;
}

export function reqNumber(raw: unknown, field: string): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    throw new ResponseMappingError(field, `expected number, got ${typeSummary(raw)}`);
  }
  return raw;
}

// --- VO 変換 (デフォルト strict) ---

/// 必須: 非 string、または create が null (検証失敗) なら throw。
export function reqVO<T>(raw: unknown, create: (value: string) => T | null, field: string): T {
  if (typeof raw !== 'string') {
    throw new ResponseMappingError(field, `expected string, got ${typeSummary(raw)}`);
  }
  const vo = create(raw);
  if (vo === null) {
    throw new ResponseMappingError(field, 'failed value-object validation');
  }
  return vo;
}

/// 任意: null/undefined は null、present かつ不正なら throw。
export function optVO<T>(
  raw: unknown,
  create: (value: string) => T | null,
  field: string,
): T | null {
  if (raw === null || raw === undefined) return null;
  return reqVO(raw, create, field);
}

/// id 配列等: reqArray + 各要素 reqVO (不正要素は throw)。
export function voArray<T>(raw: unknown, create: (value: string) => T | null, field: string): T[] {
  const arr = reqArray(raw, field);
  return arr.map((item, i) => reqVO(item, create, `${field}[${i}]`));
}

// --- Lenient 変種 (オプトイン: 汚い既存データ用。名前で「あえて緩い」を明示) ---

/// present かつ不正なら null + devWarn (throw しない)。値なしも null。
export function optVOLenient<T>(
  raw: unknown,
  create: (value: string) => T | null,
  field: string,
): T | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') {
    devWarn(field, `expected string, got ${typeSummary(raw)} — dropped`);
    return null;
  }
  const vo = create(raw);
  if (vo === null) {
    devWarn(field, 'failed value-object validation — dropped');
    return null;
  }
  return vo;
}

/// 配列: 不正要素を drop + devWarn (record 全体は落とさない)。
export function voArrayDropInvalid<T>(
  raw: unknown,
  create: (value: string) => T | null,
  field: string,
): T[] {
  const arr = reqArray(raw, field);
  const out: T[] = [];
  arr.forEach((item, i) => {
    const vo = optVOLenient(item, create, `${field}[${i}]`);
    if (vo !== null) out.push(vo);
  });
  return out;
}
